import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ReasonDialog } from "@/features/invoicing/components/ReasonDialog";
import { useNotification } from "@/shared/hooks/useNotification";
import {
  useApproveAgroforestryReceipt,
  usePendingAgroforestryReceipts,
} from "../hooks/useAgroforestry";
import {
  PROVENANCE,
  provenanceLabel,
  type AgroforestryReceiptDto,
} from "../services/agroforestryApi";

const formatDate = (value?: string) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatNumber = (value: number) =>
  value.toLocaleString("en-KE", { maximumFractionDigits: 2 });

export const AgroforestryReceiptApprovalsPage = () => {
  const notify = useNotification();
  const pendingQuery = usePendingAgroforestryReceipts();
  const approveMutation = useApproveAgroforestryReceipt();

  const [selected, setSelected] = useState<AgroforestryReceiptDto | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const rows = pendingQuery.data ?? [];

  const onApprove = (item: AgroforestryReceiptDto) => {
    approveMutation.mutate(
      { id: item.id, dto: { isApproved: true } },
      {
        onSuccess: () => notify.success(`Receipt ${item.receiptNumber} approved.`),
        onError: () => notify.error("Failed to approve receipt."),
      },
    );
  };

  const onRejectConfirm = (reason: string) => {
    if (!selected) return;
    approveMutation.mutate(
      { id: selected.id, dto: { isApproved: false, rejectionReason: reason } },
      {
        onSuccess: () => {
          notify.success(`Receipt ${selected.receiptNumber} rejected.`);
          setRejectOpen(false);
          setSelected(null);
        },
        onError: () => notify.error("Failed to reject receipt."),
      },
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Agroforestry Receipt Approvals</h1>
        <p className="mt-1 text-muted-foreground">
          Review pending tree-seedling receipts. Approving creates greenhouse stock entries with
          <code className="mx-1">Source = Agroforestry</code> in healing state — the agroforestry
          team marks them ready for sale once they establish.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <CardTitle>Pending receipts</CardTitle>
            {rows.length > 0 && (
              <Badge className="border-orange-400 bg-orange-100 text-orange-700">{rows.length}</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {pendingQuery.isLoading && (
            <p className="py-8 text-center text-muted-foreground">Loading...</p>
          )}
          {!pendingQuery.isLoading && rows.length === 0 && (
            <p className="py-8 text-center text-muted-foreground">No pending receipts.</p>
          )}
          {rows.length > 0 && (
            <div className="rounded-lg border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Receipt #</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Received</TableHead>
                    <TableHead>Supplier / Notes</TableHead>
                    <TableHead>Payment ref</TableHead>
                    <TableHead className="text-right">Total pieces</TableHead>
                    <TableHead className="text-right">Total cost</TableHead>
                    <TableHead>Requested by</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((item) => (
                    <>
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">
                          <button
                            type="button"
                            className="text-left underline-offset-2 hover:underline"
                            onClick={() =>
                              setExpanded((prev) => ({ ...prev, [item.id]: !prev[item.id] }))
                            }
                          >
                            {item.receiptNumber}
                          </button>
                        </TableCell>
                        <TableCell>
                          {item.provenance === PROVENANCE.InHousePropagation ? (
                            <Badge className="border-sky-400 bg-sky-100 text-sky-700">
                              {provenanceLabel(item.provenance)}
                            </Badge>
                          ) : (
                            <Badge variant="outline">{provenanceLabel(item.provenance)}</Badge>
                          )}
                        </TableCell>
                        <TableCell>{formatDate(item.receivedDate)}</TableCell>
                        <TableCell>
                          {item.provenance === PROVENANCE.InHousePropagation
                            ? <span className="text-muted-foreground">{item.notes ?? "In-house"}</span>
                            : (item.supplierName ?? "—")}
                        </TableCell>
                        <TableCell>
                          {item.provenance === PROVENANCE.InHousePropagation ? "—" : (item.paymentReference ?? "—")}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatNumber(item.totalQuantity)}
                        </TableCell>
                        <TableCell className="text-right">
                          {item.totalCost != null ? `KES ${formatNumber(item.totalCost)}` : "—"}
                        </TableCell>
                        <TableCell className="text-sm">{item.requestedByUserName}</TableCell>
                        <TableCell className="text-sm">{formatDate(item.requestedAt)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              onClick={() => onApprove(item)}
                              disabled={approveMutation.isPending}
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-300 text-red-600 hover:bg-red-50"
                              onClick={() => {
                                setSelected(item);
                                setRejectOpen(true);
                              }}
                              disabled={approveMutation.isPending}
                            >
                              Reject
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      {expanded[item.id] && (
                        <TableRow key={`${item.id}-lines`}>
                          <TableCell colSpan={10} className="bg-muted/40 p-4">
                            <ReceiptLinesTable receipt={item} />
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <ReasonDialog
        open={rejectOpen}
        onOpenChange={(o) => {
          setRejectOpen(o);
          if (!o) setSelected(null);
        }}
        title="Reject receipt"
        description="Provide a reason — it will be shared with the requester."
        reasonLabel="Rejection reason"
        reasonPlaceholder="e.g. payment reference doesn't match cheque book"
        submitLabel="Reject"
        isSubmitting={approveMutation.isPending}
        onSubmit={onRejectConfirm}
      />
    </div>
  );
};

const ReceiptLinesTable = ({ receipt }: { receipt: AgroforestryReceiptDto }) => (
  <div className="rounded border bg-background">
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Crop</TableHead>
          <TableHead>Variety</TableHead>
          <TableHead className="text-right">Quantity</TableHead>
          <TableHead className="text-right">Unit cost</TableHead>
          <TableHead className="text-right">Line cost</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {receipt.lines.map((l) => (
          <TableRow key={l.id}>
            <TableCell>{l.cropTypeName}</TableCell>
            <TableCell>{l.cropVarietyName}</TableCell>
            <TableCell className="text-right">{formatNumber(l.quantity)}</TableCell>
            <TableCell className="text-right">
              {l.unitCost != null ? `KES ${formatNumber(l.unitCost)}` : "—"}
            </TableCell>
            <TableCell className="text-right">
              {l.unitCost != null ? `KES ${formatNumber(l.unitCost * l.quantity)}` : "—"}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    {receipt.notes ? (
      <p className="border-t p-3 text-sm text-muted-foreground">
        <strong>Notes:</strong> {receipt.notes}
      </p>
    ) : null}
  </div>
);

export default AgroforestryReceiptApprovalsPage;
